import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetUserBundlesQuery } from '@features/subscriptions';
import { useIsAuthenticated, useUserState } from '@features/user';
import { UTMTracker } from '@services/utm';
import { useStatsigUser } from '@statsig/react-bindings';
import { parseUTCDate } from '@utils/dateUtils';

import { CenteredSpinner } from '../../../components/ui/feedback/CenteredSpinner';

/**
 * Gates rendering until Statsig is initialized with the authenticated user.
 *
 * This component should wrap content that uses feature gates and needs
 * accurate user targeting. It:
 * 1. Waits for user data from Auth0/API
 * 2. Updates Statsig with real user and waits for network response
 * 3. Only then renders children
 *
 * This ensures feature gates are evaluated with the correct user context,
 * preventing "checks from cache" warnings in Statsig admin.
 */
export const StatsigUserGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { user: auth0User } = useAuth0();
  const userState = useUserState({
    skip: !isAuthenticated,
  });
  const { data: userBundlesData } = useGetUserBundlesQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { updateUserAsync } = useStatsigUser();
  const previousHashRef = useRef<string | null>(null); // Track last synced hash

  // Extract tier and add-ons from bundle response
  const userBundles = useMemo(() => {
    if (!userBundlesData?.bundles) return { tier: null, addons: [] };

    const tier = userBundlesData.bundles.find(b => b.bundle_type === 'tier');
    const addons = userBundlesData.bundles
      .filter(b => b.bundle_type === 'add_on' || b.bundle_type === 'data_security')
      .map(b => b.bundle_id);

    return {
      tier: tier?.bundle_id ?? null,
      addons,
    };
  }, [userBundlesData]);

  // Create stable hash of targeting-relevant fields
  // This ensures we re-sync when attributes that affect experiment targeting change
  const statsigUserHash = useMemo(() => {
    if (!userState?.user_id) return null;

    return JSON.stringify({
      userID: userState.user_id,
      organizationID: userState.organization_id,
      email: userState.userInfo?.email || auth0User?.email,
      roles: userState.roles,
      paymentStatus: userState.paymentStatus,
      is_new_user: !userState.onboardingSteps?.account_creation_onboarding_completed,
      emailVerified: userState.emailVerified,
      groupIds: userState.group_ids,
      organizationSize: userState.organization_info?.size,
      tier: userBundles.tier,
      addons: userBundles.addons,
    });
  }, [userState, auth0User?.email, userBundles]);

  useEffect(() => {
    // If not authenticated, render immediately (anonymous user is fine)
    if (!isAuthenticated) {
      setIsReady(true);
      previousHashRef.current = null; // Reset on logout
      return;
    }

    // Wait for user data to load
    if (!statsigUserHash) {
      return;
    }

    // Skip if hash hasn't changed (no need to re-sync)
    if (statsigUserHash === previousHashRef.current) {
      if (!isReady) {
        setIsReady(true);
      }
      return;
    }

    // At this point, statsigUserHash exists, so userState must exist and have user_id
    if (!userState || !userState.user_id) {
      return;
    }

    const currentUserId = userState.user_id;

    // Get current UTM parameters
    const currentUTMs = UTMTracker.getUTMs();

    const statsigUser = {
      userID: currentUserId,
      customIDs: {
        organizationID: userState.organization_id ?? undefined,
      },
      email: userState.userInfo?.email || auth0User?.email,
      custom: {
        // User properties
        specialization: userState.userInfo?.specialization,
        roles: userState.roles,

        // Account status
        paymentStatus: userState.paymentStatus,
        emailVerified: userState.emailVerified,

        // Group information
        groupIds: userState.group_ids, // TODO: remove once we migrate Statsig to use organizationID

        // Organization information
        organization_size: userState.organization_info?.size,

        // Additional metadata
        patientMatchEnrolled: userState.patientMatchEnrolled,
        clearinghouseLinked: userState.clearinghouseLinked,

        // New user identification for experiments
        created_at: userState.createdAt ?? undefined,
        is_new_user: !userState.onboardingSteps?.account_creation_onboarding_completed,
        signup_timestamp: userState.createdAt
          ? Math.floor(parseUTCDate(userState.createdAt).getTime() / 1000)
          : undefined,
        days_since_signup: userState.createdAt
          ? Math.floor(
              (Date.now() - parseUTCDate(userState.createdAt).getTime()) / (1000 * 60 * 60 * 24),
            )
          : undefined,

        // UTM Parameters for attribution
        ...currentUTMs,

        // Feature flags
        ...userState.feature_flags,

        // Subscription bundles
        subscription_tier: userBundles.tier ?? undefined,
        subscription_addons: userBundles.addons,
      },
    };

    // Update Statsig with real user and WAIT for it to complete
    updateUserAsync(statsigUser)
      .then(() => {
        previousHashRef.current = statsigUserHash; // Mark as synced with current hash
        setIsReady(true);
      })
      .catch((error: unknown) => {
        console.error('[StatsigUserGate] Failed to update Statsig user:', error);
        // Proceed even on error to avoid blocking the app
        previousHashRef.current = statsigUserHash; // Mark as attempted
        setIsReady(true);
      });
  }, [
    isAuthenticated,
    statsigUserHash,
    updateUserAsync,
    isReady,
    userState,
    auth0User?.email,
    userBundles,
  ]); // Hash captures targeting-relevant changes

  if (!isReady) {
    return <CenteredSpinner sx={{ height: '100dvh' }} />;
  }

  return <>{children}</>;
};
